import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { investorProfileQueryOptions } from "creso-core/hooks";
import {
  findSecondHolder,
  SECOND_HOLDER_STEPS,
  secondHolderToFormData,
} from "creso-core/utils";
import { AddSecondHolderFormData } from "creso-core/types";
import { MultiStepForm } from "web-design-system";
import { useSecondHolderClientId } from "@crm/hooks/use-second-holder";
import { WizardProgressBar } from "./wizard-progress-bar";
import { PersonalInfoStep } from "./personal-info-step";
import { ContactDetailsStep } from "./contact-details-step";
import { OtherInfoStep } from "./other-info-step";
import { NotifyHoldersStep } from "./notify-holders-step";
import { HolderApprovalStatus } from "./holder-approval-status";

function DialogTitle({ children }: { children: string }) {
  return <span className="shrink-0 text-lg font-semibold">{children}</span>;
}

export function AddSecondHolderForm({ onClose }: { onClose: () => void }) {
  const clientId = useSecondHolderClientId();
  const { context } = useSearch({ strict: false });
  const { data: profiles, isPending } = useQuery(
    investorProfileQueryOptions(
      { client_id: clientId },
      { enabled: clientId > 0 }
    )
  );

  const secondHolder = findSecondHolder(profiles);
  const initialData = useMemo(
    () => secondHolderToFormData(secondHolder),
    [secondHolder]
  );

  const dataStepsComplete = useMemo(
    () =>
      SECOND_HOLDER_STEPS.filter((step) => step.id !== "notify-holders").every(
        (step) => step.completed(initialData)
      ),
    [initialData]
  );

  const [showWizard, setShowWizard] = useState(false);

  // Editing an already-complete second holder (via the pencil on the
  // approval status view) skips the Notify Holders step entirely — saving
  // the last step returns straight back to the approval status view.
  const isEditingCompletedSecondHolder = showWizard && dataStepsComplete;

  const defaultStep = useMemo(() => {
    if (isEditingCompletedSecondHolder) return 0;

    const incompleteStepIndex = SECOND_HOLDER_STEPS.findIndex(
      (step) => !step.completed(initialData)
    );
    return incompleteStepIndex === -1
      ? SECOND_HOLDER_STEPS.length - 1
      : incompleteStepIndex;
  }, [initialData, isEditingCompletedSecondHolder]);

  if (clientId > 0 && isPending) {
    return (
      <>
        <DialogTitle>Add Second Holder</DialogTitle>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      </>
    );
  }

  // The read-only approval status is only for the "approval pending" banner;
  // every other entry point goes through the wizard.
  if (context === "approval-status" && dataStepsComplete && !showWizard) {
    return (
      <>
        <DialogTitle>Notify Holders</DialogTitle>
        <HolderApprovalStatus
          onClose={onClose}
          onEditSecondHolder={() => setShowWizard(true)}
        />
      </>
    );
  }

  const steps = [
    <PersonalInfoStep
      key="personal-info"
      lastStepIndex={isEditingCompletedSecondHolder ? 2 : 3}
      onExistingClientSaved={
        isEditingCompletedSecondHolder ? () => setShowWizard(false) : undefined
      }
    />,
    <ContactDetailsStep key="contact-details" />,
    <OtherInfoStep
      key="other-info"
      isFinalStep={isEditingCompletedSecondHolder}
    />,
  ];
  if (!isEditingCompletedSecondHolder) {
    steps.push(<NotifyHoldersStep key="notify-holders" onClose={onClose} />);
  }

  return (
    <>
      <DialogTitle>
        {isEditingCompletedSecondHolder
          ? "Edit second holder details"
          : "Add Second Holder"}
      </DialogTitle>
      <MultiStepForm<AddSecondHolderFormData>
        initialData={initialData}
        defaultStep={defaultStep}
        progressBar={({ currentStep }) => (
          <WizardProgressBar
            currentStep={currentStep}
            totalSteps={steps.length}
          />
        )}
        onFinish={
          isEditingCompletedSecondHolder
            ? () => setShowWizard(false)
            : undefined
        }
      >
        {steps}
      </MultiStepForm>
    </>
  );
}
